import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ArtemisTaskTemplateFilterInput,
	type ArtemisTaskTemplateSortInput,
	CreateTaskTemplateDocument,
	type CreateTaskTemplateInput,
	DeleteTaskTemplateDocument,
	GetTaskTemplateDocument,
	GetTaskTemplatesDocument,
	UpdateTaskTemplateDocument,
	type UpdateTaskTemplateInput,
} from "@/common/artemis-sdk/graphql";
import type { TaskTemplate } from "../model";
import { useGqlClient } from "../use-gql-client";
import { buildWhere, toOrder, usePagedList } from "./use-paged-list";

/**
 * Server-side paginated task template list: Next/Prev cursor pagination plus
 * server-side search (name/id) and sort.
 */
export function useTaskTemplatesPaged(
	workspaceId: string,
	initialSearch?: string,
) {
	const gqlFetch = useGqlClient();
	return usePagedList<TaskTemplate>({
		baseKey: ["taskTemplates", workspaceId],
		initialSearch,
		enabled: !!workspaceId,
		initialSort: [{ id: "updatedAt", desc: true }],
		fetchPage: async ({ debouncedSearch, filters, sort, window }) => {
			const data = await gqlFetch(GetTaskTemplatesDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisTaskTemplateSortInput>(sort),
				where: buildWhere(debouncedSearch, ["name", "id"], filters) as
					| ArtemisTaskTemplateFilterInput
					| undefined,
			});
			const c = data.taskTemplates;
			return {
				nodes: (c?.nodes as TaskTemplate[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

/** Fetches a single task template by id. */
export function useTaskTemplate(taskTemplateId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["taskTemplate", taskTemplateId],
		queryFn: async () => {
			const data = await gqlFetch(GetTaskTemplateDocument, { taskTemplateId });
			return data.taskTemplate as TaskTemplate | null;
		},
		enabled: !!taskTemplateId,
	});
}

/** Creates a new task template. */
export function useCreateTaskTemplate() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: async (input: CreateTaskTemplateInput) => {
			const data = await gqlFetch(CreateTaskTemplateDocument, { input });
			return data.createTaskTemplate.taskTemplate;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["taskTemplates"] });
		},
	});
	return mutate;
}

/** Updates an existing task template. Omitted fields are left unchanged. */
export function useUpdateTaskTemplate() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: async ({
			taskTemplateId,
			input,
		}: {
			taskTemplateId: string;
			input: UpdateTaskTemplateInput;
		}) => {
			const data = await gqlFetch(UpdateTaskTemplateDocument, {
				taskTemplateId,
				input,
			});
			return data.updateTaskTemplate.taskTemplate;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["taskTemplates"] });
		},
	});
	return mutate;
}

/** Deletes one or more task templates. */
export function useDeleteTaskTemplates() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (taskTemplateIds: string[]) => {
			await Promise.all(
				taskTemplateIds.map((taskTemplateId) =>
					gqlFetch(DeleteTaskTemplateDocument, { taskTemplateId }),
				),
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["taskTemplates"] });
		},
	});
}
